import { useAtomValue } from 'jotai';
import { useContext, useEffect, useRef } from 'react';

import { MeasureSchemaType } from '@grow-therapy-team/seedling-components';

import { TelehealthSessionInfoData_TelehealthSessionInfo_Appointment } from '../../hooks';
import { StatsigLoadingContext } from '../../statsig/StatsigProvider';
import { useGetClientSafetyGuard } from '../../useGetClientSafetyGuard';
import { useGetShouldShowReflectionTopics } from '../reflections/useGetShouldShowReflectionTopics';
import { VisitorDrawerState, visitorMeasuresAtom } from '../state';
import {
  PreSessionPromptWrapper as PreSessionPrompt,
  PreSessionPromptVariant,
} from './pre-session-prompt/PreSessionPrompt';
import { usePreSessionPromptState } from './pre-session-prompt/usePreSessionPromptState';

type SessionInfoSectionWrapperProps = {
  appointment?: TelehealthSessionInfoData_TelehealthSessionInfo_Appointment | null;
  openDrawer: (state: VisitorDrawerState) => void;
};

export function SessionInfoSectionWrapper({ appointment, ...props }: SessionInfoSectionWrapperProps) {
  const { shouldShowReflectionTopics, isLoading: isLoadingShouldShowReflectionTopics } =
    useGetShouldShowReflectionTopics();
  const visitorMeasures = useAtomValue(visitorMeasuresAtom);
  const hasForms = !!visitorMeasures?.length;
  const formsAreLoading = visitorMeasures === undefined;
  const { loading: isLoadingSafetyGuardStatus, requiresSafetyGuards } = useGetClientSafetyGuard();
  const isLoading =
    useContext(StatsigLoadingContext) ||
    isLoadingShouldShowReflectionTopics ||
    formsAreLoading ||
    isLoadingSafetyGuardStatus;
  const { preSessionPrompt, setPreSessionPrompt } = usePreSessionPromptState();
  const hasClearedLocalStorageRef = useRef(false); // to prevent re-computation

  const measures = visitorMeasures ?? [];
  const visitorMeasuresAreAllScreeners =
    measures.length > 0 &&
    measures.reduce(
      (prev, currMeasure) =>
        prev &&
        !!currMeasure &&
        (currMeasure.schema.type === MeasureSchemaType.GAD7_WITH_SCREENER ||
          currMeasure.schema.type === MeasureSchemaType.PHQ9_WITH_SCREENER),
      true,
    );

  useEffect(
    function setPreSessionPromptState() {
      if (hasClearedLocalStorageRef.current) return;
      if (isLoading) return;

      // Priority order:
      // 1. Measures CTA
      // 2. Vibes (if no other pre-session prompt applies). This is the only
      //    option for clients who require safety guards, who are not eligible
      //    to fill out any forms.

      if (preSessionPrompt !== undefined) {
        // once set, always set
        return;
      } else {
        setPreSessionPrompt(PreSessionPromptVariant.VIBES);
      }
    },
    [
      isLoading,
      appointment?.timeStart,
      hasForms,
      preSessionPrompt,
      requiresSafetyGuards,
      setPreSessionPrompt,
      shouldShowReflectionTopics,
      visitorMeasuresAreAllScreeners,
    ],
  );

  return (
    <div className='flex flex-col items-center gap-y-2 p-4'>
      <PreSessionPrompt isLoading={isLoading} variant={preSessionPrompt} {...props} />
    </div>
  );
}
